import { useState } from "react"
import { BackLink, SiteHeader } from "@/components/ui-kit"
import { useNavigate, useParams } from "react-router-dom"
import { useInvoices, useInvoicesByContract } from "@/hooks/invoices/useInvoices"
import { Card } from "@/components/ui-kit/card"
import { InvoiceLine } from "@/components/invoices/invoices-line"
import { useContract } from "@/hooks/contracts/useContracts"
import { ContractsPayDialog } from "@/components/contracts/modals/contracts-pay-dialog"
import type { Invoice } from "@/types/invoice"
import { useContracts } from "@/hooks/contracts/useContracts"
import { Loader2 } from "lucide-react"
import { Button as UiKitButton } from "@/components/ui-kit/button"
import type { ApiError } from "@/types/api"

function InvoicesEmptyState({ message }: { message: string }) {
  return (
    <Card className="sm:px-6 px-4 py-6">
      <p className="text-center text-lg leading-6 text-muted-foreground">{message}</p>
    </Card>
  )
}

function InvoicesLoadingState() {
  return (
    <Card className="flex items-center justify-center sm:px-6 px-4 py-10">
      <Loader2 className="size-8 animate-spin text-brand" />
    </Card>
  )
}

function InvoicesFailedState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <Card className="flex flex-col items-center gap-4 sm:px-6 px-4 py-6">
      <p className="text-center text-lg leading-6 text-muted-foreground">{message}</p>
      {onRetry ? (
        <UiKitButton type="button" variant="secondary" onClick={onRetry}>
          Повторить
        </UiKitButton>
      ) : null}
    </Card>
  )
}

// Rent, its fee and a refund are billed on the contract; a rack's or an insurance's entityId is its line.
const CONTRACT_ENTITIES = ["rent", "rent_fee", "rent_refund"]

function AllInvoices({ onPay }: { onPay: (invoice: Invoice) => void }) {
  const { data, isLoading, isError, refetch } = useInvoices({ criteria: { conditions: [["status", "eq", "paid"]], mode: "and" }, sort: ["createdAt desc"] });
  const invoices = data?.items ?? [];
  const contractIds = [...new Set(invoices.filter(invoice => CONTRACT_ENTITIES.includes(invoice.entityType)).map(invoice => invoice.entityId))];
  const contracts = useContracts(
    { criteria: { conditions: [["id", "eq", contractIds]], mode: "and" } },
    { enabled: contractIds.length > 0 },
  );
  const unitOf = (invoice: Invoice) =>
    CONTRACT_ENTITIES.includes(invoice.entityType)
      ? contracts.data?.items?.find(contract => contract.id === invoice.entityId)?.unit
      : undefined

  if (isLoading) {
    return <InvoicesLoadingState />
  }

  if (isError) {
    return <InvoicesFailedState message="Не удалось загрузить счета" onRetry={() => void refetch()} />
  }

  if (invoices.length === 0) {
    return <InvoicesEmptyState message="Пока нет оплаченных счетов" />
  }

  return (
    <Card className="sm:px-6 px-4 py-6">
      {invoices.map((invoice) => (
        <InvoiceLine key={invoice.id} invoice={invoice} onPay={() => onPay(invoice)} unitCode={unitOf(invoice)} />
      ))}
    </Card>
  );
}

function ContractInvoices({
  contractId,
  onPay,
}: {
  contractId: number
  onPay: (invoice: Invoice) => void
}) {
  const { data, isLoading, isError, error, refetch } = useInvoicesByContract(contractId);
  const invoices = data?.items ?? [];

  if (!Number.isInteger(contractId) || contractId <= 0 || (error as ApiError | null)?.code === "404") {
    return <InvoicesFailedState message="Бокс не найден" />
  }

  if (isLoading) {
    return <InvoicesLoadingState />
  }

  if (isError) {
    return <InvoicesFailedState message="Не удалось загрузить счета" onRetry={() => void refetch()} />
  }

  if (invoices.length === 0) {
    return <InvoicesEmptyState message="У этого бокса пока нет счетов" />
  }

  return (
    <Card className="sm:px-6 px-4 py-6">
      {invoices.map((invoice) => (
        <InvoiceLine key={invoice.id} invoice={invoice} showStatus onPay={() => onPay(invoice)} />
      ))}
    </Card>
  );
}

export function InvoicesPage() {
  const navigate = useNavigate()
  const { contractId: contractIdParam } = useParams();
  const routeContractId = contractIdParam ? Number(contractIdParam) : undefined
  const { data: contract } = useContract(routeContractId ?? 0)
  const [isPayDialogOpen, setIsPayDialogOpen] = useState(false)
  const [invoicesToPay, setInvoicesToPay] = useState<Invoice[]>([])

  const invoiceContractId = invoicesToPay[0]?.entityType === "contract"
    ? invoicesToPay[0].entityId
    : undefined
  const payContractId = routeContractId ?? invoiceContractId
  const { data: payContract } = useContract(
    isPayDialogOpen && payContractId && !routeContractId ? payContractId : 0,
  )

  const resolvedContract = contract ?? payContract

  const handlePayInvoice = (invoice: Invoice) => {
    setInvoicesToPay([invoice])
    setIsPayDialogOpen(true)
  }

  return (
    <div className="min-h-screen bg-background sm:pt-20 pt-12">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[722px] px-2 pt-8 pb-16 md:px-4">
        <section className="sm:px-0 px-4">
          <BackLink onClick={() => navigate('/home')} className="sm:mb-4 mb-[6px]">
            <span>Все боксы</span>
          </BackLink>
          <h1 className="mb-6 sm:text-[48px] text-[28px] leading-[36px] sm:leading-[52px] font-semibold text-foreground">
            {contractIdParam
              ? <span>Счета бокса {contract?.unit ?? ""}</span>
              : <span>Архив счетов</span>
            }
          </h1>
        </section>

        <section>
          {contractIdParam
            ? <ContractInvoices contractId={Number(contractIdParam)} onPay={handlePayInvoice} />
            : <AllInvoices onPay={handlePayInvoice} />
          }
        </section>
      </main>

      <ContractsPayDialog
        open={isPayDialogOpen}
        onOpenChange={(open) => {
          setIsPayDialogOpen(open)
          if (!open) {
            setInvoicesToPay([])
          }
        }}
        invoices={invoicesToPay}
        contractId={resolvedContract?.id ?? payContractId}
        isRecurrent={resolvedContract?.isRecurrent}
      />
    </div>
  )
}
